import { Autocomplete, TextField } from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import { ControlJumpEntry, rankControlMatches } from './control-jump';

import { getDesignTokens } from '~c/components/base/core';

interface ControlJumpSearchProps {
  className?: string;
  entries: ControlJumpEntry[];
  onJump: (entry: ControlJumpEntry) => void;
}

const optionKey = (entry: ControlJumpEntry) =>
  `${entry.bundleId}:${entry.identifier}`;

const OptionBody = muiStyled('div')(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    width: 100%;

    .control-jump-option-id {
      font-family: monospace;
      font-size: 0.8125rem;
      font-weight: 600;
      color: ${t.textPrimary};
      overflow-wrap: anywhere;
    }

    .control-jump-option-bundle {
      font-size: 0.6875rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${t.textMuted};
    }

    .control-jump-option-question {
      font-size: 0.8125rem;
      line-height: 1.35;
      color: ${t.textSecondary};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `;
});

const ControlJumpSearchImpl: React.FC<ControlJumpSearchProps> = ({
  className,
  entries,
  onJump,
}) => {
  const inputId = React.useId();
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);

  return (
    <div className={className}>
      <label className="control-jump-label" htmlFor={inputId}>
        Jump to control
      </label>
      <Autocomplete<ControlJumpEntry, false, false, false>
        id={inputId}
        size="small"
        options={entries}
        value={null}
        inputValue={query}
        onInputChange={(_event, value, reason) => {
          if (reason !== 'reset') {
            setQuery(value);
          }
        }}
        open={open && query.trim() !== ''}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        filterOptions={(options, { inputValue }) =>
          rankControlMatches(options, inputValue)
        }
        getOptionLabel={(option) => option.identifier}
        isOptionEqualToValue={(a, b) => optionKey(a) === optionKey(b)}
        onChange={(_event, entry) => {
          if (entry) {
            setQuery('');
            setOpen(false);
            onJump(entry);
          }
        }}
        autoHighlight
        blurOnSelect
        clearOnEscape
        forcePopupIcon={false}
        handleHomeEndKeys={false}
        noOptionsText="No matching control"
        slotProps={{
          popper: {
            placement: 'bottom-start',
            style: { width: 'min(380px, calc(100vw - 32px))' },
          },
          paper: {
            sx: {
              '& .MuiAutocomplete-option.Mui-focused': {
                boxShadow: (theme) =>
                  `inset 3px 0 0 ${theme.palette.primary.main}`,
              },
            },
          },
        }}
        renderOption={(props, option) => (
          <li {...props} key={optionKey(option)}>
            <OptionBody>
              <span className="control-jump-option-id">
                {option.identifier}
              </span>
              <span className="control-jump-option-bundle">
                {option.bundleName}
              </span>
              {option.question && (
                <span className="control-jump-option-question">
                  {option.question}
                </span>
              )}
            </OptionBody>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder="ID or question text" />
        )}
      />
    </div>
  );
};

export const ControlJumpSearch = muiStyled(ControlJumpSearchImpl)(({
  theme,
}) => {
  const nav = getDesignTokens(theme.palette.mode, theme.accentPalette).nav;
  return css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)} ${theme.spacing(5)};

    .control-jump-label {
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${nav.itemTextOnDarkMuted};
    }

    .MuiInputBase-input {
      font-size: 0.8125rem;
    }
  `;
});

export default ControlJumpSearch;
